/**
 * Overview model — the defensibility chain, computed from data the banking
 * surfaces already return. Pure functions, no fetching, so every state the
 * page can enter is directly testable.
 *
 * The page thesis it serves:
 *   ARC defines what should be true.
 *   Compass shows what is known, missing, or contradicted.
 *   The bank makes the decision.
 *
 * Honesty rules (carried from the inventory and the Library):
 *  - An unread ARC-mapping read leaves scope counts UNKNOWN (null), never
 *    zero. "0 scoped" and "we could not ask" are opposite claims.
 *  - A stage the product has not built yet reports that it is not built. It
 *    never reports an empty result, because an empty result is a measurement.
 *  - Nothing here derives a finding, a score, or a materiality tier. ARC owns
 *    what should be true; this module only counts what the bank has recorded.
 *
 * ARC IP boundary: mapping-STATE identifiers only. No control text, no test
 * text, no workbook content.
 */

import type { ImportSummary, UseCaseSummary } from './useCaseVocabulary';

/** The five typed ARC mapping states the server derives (DC-1782). */
const SCOPED_STATES = ['partially_mapped', 'stale_version', 'reviewer_unconfirmed', 'validated'];

export interface ProgramSummary {
  /** Registered AI use cases — the bank's own record of what runs. */
  registered: number;
  inProduction: number;
  /** null = the ARC mapping-state read failed; the truth is unknown, not zero. */
  scopeKnown: boolean;
  scoped: number | null;
  needScope: number | null;
  awaitingValidation: number | null;
  staleVersion: number | null;
  validated: number | null;
  /** Recorded human decisions — the only stage the bank, not Compass, owns. */
  decisionsCurrent: number;
  /**
   * Real assessment state, counted from runs (DC-1794). null = the read
   * failed, and unknown is not zero — the chain refuses to state a position
   * it did not measure, exactly as it does for scope.
   */
  assessmentKnown: boolean;
  subjectsAssessed: number | null;
  runsWithDecision: number | null;
  /** Rejected rows sitting in the most recent import, if any. */
  importIssues: { importId: string; filename: string; rejected: number } | null;
  /** Use cases most recently changed, newest first (bounded by the caller). */
  recentlyChanged: UseCaseSummary[];
}

export interface AssessmentSummary {
  runsComplete: number;
  subjectsAssessed: number;
  runsWithDecision: number;
}

export function summarizeProgram(params: {
  useCases: UseCaseSummary[];
  arcStates: Record<string, string>;
  arcStatesKnown: boolean;
  latestImport: ImportSummary | null;
  /** null = the assessment read failed; the truth is unknown, not zero. */
  assessment?: AssessmentSummary | null;
  recentLimit?: number;
}): ProgramSummary {
  const {
    useCases,
    arcStates,
    arcStatesKnown,
    latestImport,
    assessment = null,
    recentLimit = 4,
  } = params;

  const countState = (predicate: (state: string | undefined) => boolean): number | null =>
    arcStatesKnown ? useCases.filter((uc) => predicate(arcStates[uc.id])).length : null;

  return {
    registered: useCases.length,
    inProduction: useCases.filter((uc) => uc.lifecycleStage === 'production').length,
    scopeKnown: arcStatesKnown,
    scoped: countState((s) => Boolean(s) && SCOPED_STATES.includes(s as string)),
    // Absent entry and an explicit 'unmapped' are the same fact: no scope yet.
    needScope: countState((s) => !s || s === 'unmapped'),
    awaitingValidation: countState((s) => s === 'reviewer_unconfirmed' || s === 'partially_mapped'),
    staleVersion: countState((s) => s === 'stale_version'),
    validated: countState((s) => s === 'validated'),
    decisionsCurrent: useCases.filter((uc) => uc.recordStatus === 'decision_current').length,
    assessmentKnown: assessment !== null,
    subjectsAssessed: assessment ? assessment.subjectsAssessed : null,
    runsWithDecision: assessment ? assessment.runsWithDecision : null,
    importIssues:
      latestImport && latestImport.rowCountRejected > 0
        ? {
            importId: latestImport.id,
            filename: latestImport.sourceFilename,
            rejected: latestImport.rowCountRejected,
          }
        : null,
    recentlyChanged: [...useCases]
      .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0))
      .slice(0, recentLimit),
  };
}

export type NextDecisionId =
  | 'scope_unreadable'
  | 'no_inventory'
  | 'scope_missing'
  | 'scope_stale'
  | 'scope_unconfirmed'
  | 'assessment_ready'
  | 'decision_owed'
  | 'assessment_current';

export interface NextDecision {
  id: NextDecisionId;
  headline: string;
  detail: string;
  /** null when the honest next step is not a destination this build can offer. */
  action: { label: string; to: string } | null;
  /** Set when the next step exists in the product plan but not in the product. */
  inDevelopment?: boolean;
}

/**
 * The single next decision the organization owes, derived from its own state.
 *
 * Ordering is a claim about consequence, not about workflow tidiness:
 *   1. nothing registered      — there is no program to reason about yet
 *   2. no scope                — the use case is running against no control baseline
 *   3. scope built on an older catalog — a wrong basis outranks an unreviewed one
 *   4. scope awaiting a human  — queued, and only a person can clear it
 *   5. everything validated, nothing assessed — run the assessment (D4 made
 *      this real; the old terminal branch said the engine was in development
 *      and is no longer true)
 *   6. assessed, undecided     — the finding is in front of nobody until a
 *      named human records the judgement
 */
export function nextDecision(summary: ProgramSummary): NextDecision {
  if (summary.registered === 0) {
    return {
      id: 'no_inventory',
      headline: 'Register the AI this bank runs.',
      detail:
        'The inventory is the spine of the program — every ARC scope, evidence request, and decision hangs off it. Import the completed template, or add the first use case by hand.',
      action: { label: 'Register AI use cases →', to: '/use-cases' },
    };
  }

  if (!summary.scopeKnown) {
    return {
      id: 'scope_unreadable',
      headline: 'ARC scope states could not be read.',
      detail:
        'The inventory loaded, but the mapping states did not. Nothing below is a measurement of your scope — reload to try again before acting on this page.',
      action: null,
    };
  }

  if ((summary.needScope ?? 0) > 0) {
    const n = summary.needScope as number;
    return {
      id: 'scope_missing',
      headline: `${n} use ${n === 1 ? 'case' : 'cases'} still ${n === 1 ? 'needs' : 'need'} an ARC scope.`,
      detail:
        'Confirm which risks, controls, and tests apply before assessment begins. Compass suggests the scope; the reviewer decides what is in and what is out, with a recorded reason.',
      action: { label: 'Review unscoped use cases →', to: '/use-cases?arc=unmapped' },
    };
  }

  if ((summary.staleVersion ?? 0) > 0) {
    const n = summary.staleVersion as number;
    return {
      id: 'scope_stale',
      headline: `${n} ${n === 1 ? 'scope was' : 'scopes were'} confirmed against an earlier ARC catalog.`,
      detail:
        'The catalog has moved since these were validated. Re-confirm them so the assessment runs against the baseline the bank is actually held to.',
      action: { label: 'Re-confirm affected scopes →', to: '/use-cases?arc=stale' },
    };
  }

  if ((summary.awaitingValidation ?? 0) > 0) {
    const n = summary.awaitingValidation as number;
    return {
      id: 'scope_unconfirmed',
      headline: `${n} suggested ${n === 1 ? 'scope is' : 'scopes are'} awaiting validation.`,
      detail:
        'A suggestion is not a scope until a named reviewer confirms it. Validate the suggested controls, or restrict and expand them with a reason on the record.',
      action: { label: 'Validate suggested scopes →', to: '/use-cases?arc=awaiting' },
    };
  }

  // Beyond this point the scope work is done. What the organization owes next
  // depends on assessment state — and an unread assessment state is unknown,
  // never zero, so the page says so rather than inventing a next step.
  if (!summary.assessmentKnown) {
    return {
      id: 'assessment_current',
      headline: 'Every registered use case has a validated ARC scope.',
      detail:
        'The assessment state could not be read, so this page will not tell you whether an assessment has run. Reload before acting on it.',
      action: null,
    };
  }

  if ((summary.subjectsAssessed ?? 0) === 0) {
    return {
      id: 'assessment_ready',
      headline: 'Every registered use case has a validated ARC scope.',
      detail:
        'Assessment is the next stage: each scoped test is answered from the evidence on the record, and the tests nothing can answer are stated rather than scored. Open a validated use case and run it.',
      action: { label: 'Open a validated use case →', to: '/use-cases' },
    };
  }

  if ((summary.runsWithDecision ?? 0) === 0) {
    return {
      id: 'decision_owed',
      headline: 'An assessment has run, and nobody has decided on it.',
      detail:
        'Findings are not a decision. Record the judgement, the rationale, and the accountable owner against the run — Compass preserves them; it never issues them.',
      action: { label: 'Record the decision →', to: '/use-cases' },
    };
  }

  return {
    id: 'assessment_current',
    headline: 'Scope validated, assessment run, decision recorded.',
    detail:
      'The chain is complete for what has been assessed so far. Re-assess when the evidence or the catalog moves — each run is kept, so the drift between them stays visible.',
    action: { label: 'Review the assessments →', to: '/use-cases' },
  };
}
